@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_driver

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.example.project.enginekalender.kalenderAddDays
import org.example.project.enginekalender.kalenderTodayIsoDate
import org.example.project.enginekalender.parseKalenderIsoDate
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.example.project.utils.LocalizationManager
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.js.Date

@Composable
fun DriverMeineAuftraegeLayout(
    title: String = "",
    loading: Boolean,
    error: String?,
    bookings: List<DriverBookingCardModel>,
    onOpenBooking: (DriverBookingCardModel) -> Unit
) {
    Style(DriverOrderStyles)
    val t: (String) -> String = { LocalizationManager.get(it) }
    val initialDateKey = DriverFlowState.selectedMeineAuftraegeDateKey.value
    val initialArtCode = DriverFlowState.selectedMeineAuftraegeArtCode.value
    var selectedBookingKey by remember { mutableStateOf("") }
    val weekDays = driverOrderScrollableDays(bookings, initialDateKey)
    var selectedDayOffset by remember(initialDateKey, weekDays) {
        mutableStateOf(weekDays.firstOrNull { it.dateKey == initialDateKey }?.offset ?: 0)
    }
    val selectedDay = weekDays.firstOrNull { it.offset == selectedDayOffset } ?: weekDays.first()
    val visibleBookings = bookings
        .filter { it.booking.driverOrderDateKey() == selectedDay.dateKey }
        .filter { initialArtCode.isBlank() || it.artCode == initialArtCode }
        .filter { !it.isDriverFinalAuftrag() }
        .sortedWith(
            compareBy<DriverBookingCardModel> { it.booking.driverOrderTimeKey() }
                .thenBy { it.currentTargetLabel }
                .thenBy { it.booking.lieferscheinnummer }
                .thenBy { it.booking.id }
        )
    val selectedBooking = selectedBookingKey
        .takeIf { it.isNotBlank() }
        ?.let { key -> visibleBookings.firstOrNull { driverOrderPreviewKey(it) == key } }

    DisposableEffect(initialDateKey, initialArtCode) {
        onDispose {
            DriverFlowState.selectedMeineAuftraegeDateKey.value = ""
            DriverFlowState.selectedMeineAuftraegeArtCode.value = ""
        }
    }

    DisposableEffect(selectedBookingKey) {
        if (selectedBookingKey.isNotBlank()) {
            DriverFlowState.mobileBackHandler.value = {
                selectedBookingKey = ""
                true
            }
        } else if (DriverFlowState.mobileBackHandler.value != null) {
            DriverFlowState.mobileBackHandler.value = null
        }
        onDispose {
            if (DriverFlowState.mobileBackHandler.value != null) {
                DriverFlowState.mobileBackHandler.value = null
            }
        }
    }

    Div({
        classes(DriverOrderStyles.page)
        style { property("touch-action", "pan-x pan-y") }
    }) {
        Div({ classes(DriverOrderStyles.stickyHeader) }) {
            H3({ classes(DriverOrderStyles.title) }) { Text(title.ifBlank { t("driver_my_orders") }) }
            if (selectedBooking == null) {
                DriverOrderWeekHeader(
                    days = weekDays,
                    selectedOffset = selectedDayOffset,
                    onSelect = { selectedDayOffset = it }
                )
            }
        }
        if (selectedBooking != null) {
            Div({ classes(DriverOrderStyles.detailStack) }) {
                DriverMeineAuftraegeCard(
                    card = selectedBooking,
                    clickable = false,
                    onOpen = {}
                )
                LumaticaButton(
                    label = t("driver_start_order"),
                    onClick = { onOpenBooking(selectedBooking) },
                    variant = LumaticaButtonVariant.Action
                )
            }
            return@Div
        }

        when {
            loading -> {
                Div({ classes(DriverOrderStyles.noticeCard) }) {
                    Text(t("driver_loading_orders"))
                }
            }

            error != null -> {
                Div({ classes(DriverOrderStyles.errorCard) }) {
                    Text(error)
                }
            }

            visibleBookings.isEmpty() -> {
                Div({ classes(DriverOrderStyles.noticeCard) }) {
                    Text(t("driver_no_assigned_orders"))
                }
            }

            else -> {
                Div({
                    classes(DriverOrderStyles.grid)
                }) {
                    visibleBookings.forEach { card ->
                        val cardKey = driverOrderPreviewKey(card)
                        Div({ classes(DriverOrderStyles.previewStack) }) {
                            DriverMeineAuftraegePreviewCard(
                                card = card,
                                expanded = false,
                                onToggle = {
                                    selectedBookingKey = cardKey
                                }
                            )
                        }
                    }
                }
            }
        }
    }
}

internal fun driverOrderPreviewKey(card: DriverBookingCardModel): String =
    listOf(card.providerFirmenId, card.artCode, card.booking.id, card.primaryRef)
        .joinToString("#")

internal fun driverOrderCanOpen(card: DriverBookingCardModel, kundenhofAuftrag: DriverBookingCardModel?): Boolean =
    when {
        card.isDriverFinalAuftrag() -> false
        kundenhofAuftrag == null -> true
        kundenhofAuftrag.booking.id == card.booking.id &&
            kundenhofAuftrag.providerFirmenId == card.providerFirmenId &&
            kundenhofAuftrag.artCode == card.artCode -> true
        else -> false
    }

private data class DriverOrderDay(
    val offset: Int,
    val label: String,
    val dateKey: String,
    val openCount: Int
)

@Composable
private fun DriverOrderWeekHeader(
    days: List<DriverOrderDay>,
    selectedOffset: Int,
    onSelect: (Int) -> Unit
) {
    Div({ classes(DriverOrderStyles.weekBar) }) {
        days.forEach { day ->
            Span({
                classes(DriverOrderStyles.weekDay)
                if (day.offset == selectedOffset) classes(DriverOrderStyles.weekDayActive)
                onClick { onSelect(day.offset) }
            }) {
                Text(day.label)
                Span({ classes(DriverOrderStyles.weekDayDate) }) {
                    Text(driverOrderWeekDayDateLabel(day.dateKey))
                }
                Span({ classes(DriverOrderStyles.weekDayCount) }) {
                    Text(day.openCount.toString())
                }
            }
        }
    }
}

object DriverOrderStyles : StyleSheet() {
    val page by style {
        height(100.percent)
        width(100.percent)
        padding(18.px, 16.px, 28.px, 16.px)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(14.px)
        overflowY("auto")
    }

    val title by style {
        margin(0.px)
        color(Color.white)
        fontSize(22.px)
        fontWeight("700")
        textAlign("center")
    }

    val stickyHeader by style {
        position(Position.Sticky)
        top(0.px)
        property("z-index", "5")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        padding(0.px, 0.px, 8.px, 0.px)
        backgroundColor(Color.transparent)
    }

    val searchInput by style {
        width(100.percent)
        height(42.px)
        borderRadius(8.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        backgroundColor(rgba(255, 255, 255, 0.06))
        color(Color.white)
        padding(0.px, 12.px)
        fontSize(15.px)
        fontWeight("800")
        boxSizing("border-box")
    }

    val weekBar by style {
        display(DisplayStyle.Flex)
        gap(8.px)
        alignItems(AlignItems.Center)
        width(100.percent)
        overflowX("auto")
        overflowY("hidden")
        paddingBottom(2.px)
        property("-webkit-overflow-scrolling", "touch")
        property("scroll-snap-type", "x mandatory")
    }

    val weekDay by style {
        minWidth(64.px)
        padding(8.px, 0.px)
        color(rgba(255, 255, 255, 0.68))
        fontSize(13.px)
        fontWeight("900")
        textAlign("center")
        cursor("pointer")
        property("user-select", "none")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        gap(3.px)
        flexShrink(0)
        property("scroll-snap-align", "start")
        property("scroll-snap-stop", "always")
    }

    val weekDayActive by style {
        color(UISettings.ACCENT)
    }

    val weekDayCount by style {
        fontSize(11.px)
        fontWeight("800")
        color(rgba(255, 255, 255, 0.54))
    }

    val weekDayDate by style {
        fontSize(10.px)
        fontWeight("800")
        color(rgba(255, 255, 255, 0.58))
        lineHeight("1.1")
        whiteSpace("nowrap")
    }

    val noticeCard by style {
        padding(16.px, 18.px)
        borderRadius(20.px)
        property("background", "rgba(255,255,255,0.06)")
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        color(Color.white)
    }

    val summaryGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
        gap(10.px)
        width(100.percent)
    }

    val calendarCenter by style {
        width(100.percent)
        maxWidth(520.px)
        alignSelf(AlignSelf.Center)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.Center)
        gap(14.px)
        flex(1)
    }

    val summaryCard by style {
        padding(12.px, 14.px)
        borderRadius(14.px)
        property("background", "rgba(255,255,255,0.06)")
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(4.px)
        boxSizing("border-box")
    }

    val summaryCardClickable by style {
        cursor("pointer")
    }

    val summaryLabel by style {
        color(rgba(255, 255, 255, 0.58))
        fontSize(11.px)
        fontWeight("800")
        property("text-transform", "uppercase")
    }

    val summaryValue by style {
        color(Color.white)
        fontSize(22.px)
        fontWeight("900")
    }

    val errorCard by style {
        padding(16.px, 18.px)
        borderRadius(20.px)
        backgroundColor(rgba(140, 26, 26, 0.48))
        border(1.px, LineStyle.Solid, rgba(255, 116, 116, 0.42))
        color(Color.white)
    }

    val grid by style {
        display(DisplayStyle.Grid)
        gridTemplateColumns("repeat(auto-fit, minmax(260px, 1fr))")
        gap(14.px)
        alignItems(AlignItems.Stretch)
    }

    val previewStack by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        minWidth(0.px)
    }

    val detailStack by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(14.px)
        width(100.percent)
        maxWidth(520.px)
        alignSelf(AlignSelf.Center)
    }

    val previewTile by style {
        padding(14.px, 15.px)
        borderRadius(18.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(8.px)
        color(Color.white)
        property("background", "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)")
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.14))
        property("box-shadow", "0 12px 24px rgba(0,0,0,0.20)")
        cursor("pointer")
        boxSizing("border-box")
    }

    val previewTileActive by style {
        padding(14.px, 15.px)
        borderRadius(18.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(8.px)
        color(Color.white)
        property("background", "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)")
        border(1.px, LineStyle.Solid, UISettings.ACCENT)
        property("box-shadow", "0 0 0 1px rgba(255,0,0,0.24), 0 14px 28px rgba(0,0,0,0.24)")
        cursor("pointer")
        boxSizing("border-box")
    }

    val previewTopRow by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        gap(10.px)
        minWidth(0.px)
    }

    val previewArt by style {
        color(Color.white)
        fontSize(15.px)
        fontWeight("900")
        property("text-transform", "uppercase")
    }

    val previewDate by style {
        color(rgba(255, 255, 255, 0.72))
        fontSize(12.px)
        fontWeight("800")
        whiteSpace("nowrap")
    }

    val previewCompany by style {
        color(Color.white)
        fontSize(14.px)
        fontWeight("800")
        overflow("hidden")
        property("text-overflow", "ellipsis")
        whiteSpace("nowrap")
    }

    val previewRef by style {
        color(rgba(255, 255, 255, 0.78))
        fontSize(13.px)
        fontWeight("800")
        overflow("hidden")
        property("text-overflow", "ellipsis")
        whiteSpace("nowrap")
    }

    val previewAddress by style {
        color(rgba(255, 255, 255, 0.66))
        fontSize(13.px)
        lineHeight("1.35")
        property("word-break", "break-word")
    }

    val documentLinkRow by style {
        marginTop(4.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.FlexStart)
        gap(8.px)
        color(rgba(255, 255, 255, 0.78))
        fontSize(12.px)
        fontWeight("800")
        lineHeight("1.25")
    }

    val documentLinkIcon by style {
        width(16.px)
        height(16.px)
        flexShrink(0)
        property("filter", "brightness(0) invert(1)")
    }

    val documentLinkText by style {
        minWidth(0.px)
        overflow("hidden")
        property("text-overflow", "ellipsis")
        whiteSpace("nowrap")
    }

    val documentCenter by style {
        flex(1)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        maxWidth(520.px)
        alignSelf(AlignSelf.Center)
    }

    val documentRow by style {
        width(100.percent)
        minHeight(44.px)
        padding(10.px, 14.px)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.FlexStart)
        gap(10.px)
        borderRadius(8.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.14))
        property("background", "rgba(255,255,255,0.04)")
        color(Color.white)
        cursor("pointer")
    }

    val documentRowIcon by style {
        width(18.px)
        height(18.px)
        flexShrink(0)
        property("filter", "brightness(0) invert(1)")
    }

    val documentRowText by style {
        minWidth(0.px)
        fontSize(15.px)
        fontWeight("900")
        lineHeight("1.25")
        property("word-break", "break-word")
    }

    val tile by style {
        padding(16.px)
        borderRadius(22.px)
        minHeight(170.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        color(Color.white)
        property("background", "linear-gradient(180deg, rgba(18,22,32,0.92) 0%, rgba(11,14,22,0.86) 100%)")
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.13))
        property("box-shadow", "0 16px 30px rgba(0,0,0,0.24)")
    }

    val tileTopRow by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        gap(8.px)
        flexWrap(FlexWrap.Wrap)
    }

    val artText by style {
        color(rgba(255, 255, 255, 0.82))
        fontSize(13.px)
        fontWeight("600")
    }

    val dateChip by style {
        color(rgba(255, 255, 255, 0.78))
        fontSize(12.px)
        textAlign("right")
    }

    val infoRow by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(3.px)
    }

    val infoLabel by style {
        color(rgba(255, 255, 255, 0.48))
        fontSize(11.px)
        fontWeight("700")
        property("letter-spacing", "0.08em")
        property("text-transform", "uppercase")
    }

    val infoValue by style {
        color(Color.white)
        fontSize(14.px)
        lineHeight("1.35")
        property("word-break", "break-word")
    }

}

private fun driverOrderScrollableDays(
    bookings: List<DriverBookingCardModel>,
    selectedDateKey: String = ""
): List<DriverOrderDay> {
    val groupedBookings = bookings.groupBy { it.booking.driverOrderDateKey() }
    val today = parseKalenderIsoDate(kalenderTodayIsoDate())!!
    val baseDays = (0..30).map { offset ->
        val dateKey = kalenderAddDays(
            today.year,
            today.month,
            today.day,
            offset
        )
        val dayBookings = groupedBookings[dateKey].orEmpty()
        DriverOrderDay(
            offset = offset,
            label = driverOrderDayLabel(dateKey),
            dateKey = dateKey,
            openCount = dayBookings.count { !it.isDriverFinalAuftrag() }
        )
    }
    if (selectedDateKey.isBlank() || baseDays.any { it.dateKey == selectedDateKey }) return baseDays

    if (parseKalenderIsoDate(selectedDateKey) == null) return baseDays
    val dayBookings = groupedBookings[selectedDateKey].orEmpty()
    val selectedDay = DriverOrderDay(
        offset = 31,
        label = driverOrderDayLabel(selectedDateKey),
        dateKey = selectedDateKey,
        openCount = dayBookings.count { !it.isDriverFinalAuftrag() }
    )
    return (baseDays + selectedDay).sortedWith(
        compareBy<DriverOrderDay> {
            parseKalenderIsoDate(it.dateKey)?.let { parsed ->
                listOf(
                    parsed.year.toString(),
                    parsed.month.toString(),
                    parsed.day.toString()
                ).joinToString("-")
            } ?: it.dateKey
        }
    )
}

private fun driverOrderWeekDayDateLabel(dateKey: String): String {
    val parts = dateKey.split("-")
    if (parts.size != 3) return dateKey
    return "${parts[2]}.${parts[1]}."
}

private fun driverOrderDayLabel(dateKey: String): String {
    val date = parseKalenderIsoDate(dateKey) ?: return ""
    val jsDate = Date(
        date.year,
        date.month - 1,
        date.day
    )
    return LocalizationManager.get("driver_weekday_${jsDate.getDay().toInt()}_short")
}

internal fun All.driverOrderDateKey(): String =
    driverOrderNormalizeDate(datum.ifBlank { verladedatum }).ifBlank { kalenderTodayIsoDate() }

internal fun All.driverOrderTimeKey(): String =
    uhrzeitVon.ifBlank { verladeuhrzeit }.ifBlank { uhrzeitAnmeldung }

private fun driverOrderNormalizeDate(value: String): String {
    val raw = value.trim()
    if (raw.isBlank()) return ""
    if (parseKalenderIsoDate(raw) != null) return raw

    Regex("""^(\d{2})\.(\d{2})\.(\d{4})$""").matchEntire(raw)?.let { match ->
        val (day, month, year) = match.destructured
        val iso = "$year-$month-$day"
        if (parseKalenderIsoDate(iso) != null) return iso
    }

    Regex("""^(\d{4})(\d{2})(\d{2})$""").matchEntire(raw)?.let { match ->
        val (year, month, day) = match.destructured
        val iso = "$year-$month-$day"
        if (parseKalenderIsoDate(iso) != null) return iso
    }

    return ""
}
